import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { integrationsQueryKeys } from '@/hooks/integrations/types'
import { salesforceStageMappingsQueryKeys } from '@/hooks/salesforce/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { SalesforceIntegrationUpdate } from '@/types/Integration'

export const updateSalesforceStageMappings = async (
  orgId: UUID | '',
  update: SalesforceIntegrationUpdate,
  accessToken: string,
): Promise<void> => {
  if (!orgId) {
    throw new Error('Org ID is required')
  }
  const url = `${API_URL}/api/v1/organizations/${orgId}/salesforce/opportunity-stage-mappings`
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(update),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

interface UseUpdateSalesforceStageMappingsArgs {
  orgId: UUID | ''
}

export const useUpdateSalesforceStageMappings = ({
  orgId,
}: UseUpdateSalesforceStageMappingsArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (update: SalesforceIntegrationUpdate) =>
      updateSalesforceStageMappings(orgId, update, accessToken),
    onError: (error) => {
      handleError(error)
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: salesforceStageMappingsQueryKeys.organization({ orgId }),
        }),
        queryClient.invalidateQueries({
          queryKey: integrationsQueryKeys.organization({ orgId }),
        }),
      ])
    },
  })
}
